import { useCallback, useMemo, useState } from "react";
import { useGetCompanyPreview } from "@/hooks/api/query/useGetCompanyPreview";
import type { Nis2ResultEnum } from "@/api/types/companyPreview";
import {
  findDuplicateCuiSession,
  type SessionWithEntity,
} from "@/utils/multi-session";

type CuiVerificationSectionProps = {
  sectionRef: (el: HTMLElement | null) => void;
  isLoggedIn: boolean;
  onCreateAccountWithCui: (cui: string) => void;
  isCreatingSession: boolean;
  userSessions?: SessionWithEntity[];
  archivedUserSessions?: SessionWithEntity[];
};

const NIS2_BADGE_LABELS: Record<Nis2ResultEnum, string> = {
  ESSENTIAL: "ENTITATE ESENȚIALĂ",
  IMPORTANT: "ENTITATE IMPORTANTĂ",
  IMPACT: "ENTITATE DE IMPACT (Art. 9)",
  OUT_OF_SCOPE: "ÎN AFARA SCOPULUI NIS2",
};

export function CuiVerificationSection({
  sectionRef,
  isLoggedIn,
  onCreateAccountWithCui,
  isCreatingSession,
  userSessions,
  archivedUserSessions,
}: CuiVerificationSectionProps) {
  const [cuiValue, setCuiValue] = useState("");
  const [cuiRevealed, setCuiRevealed] = useState(true);
  const [cuiIsDemo, setCuiIsDemo] = useState(true);
  const [submittedCui, setSubmittedCui] = useState<string | undefined>(
    undefined
  );

  const {
    data: rawCompanyData,
    isLoading: cuiLoading,
    error: cuiError,
  } = useGetCompanyPreview(submittedCui);

  const companyData =
    rawCompanyData?.company.cui === submittedCui ? rawCompanyData : undefined;

  const cuiMatch = useMemo(
    () =>
      isLoggedIn && submittedCui
        ? findDuplicateCuiSession({
            cui: submittedCui,
            sessions: userSessions ?? [],
            archivedSessions: archivedUserSessions ?? [],
          })
        : null,
    [isLoggedIn, submittedCui, userSessions, archivedUserSessions]
  );
  const cuiAlreadyExists = !!cuiMatch && !cuiMatch.isArchived;
  const cuiArchivedMatch = !!cuiMatch && cuiMatch.isArchived;

  const handleCuiVerify = useCallback(() => {
    const cleaned = cuiValue.replace(/^RO/i, "").trim();
    if (cleaned.length >= 4) {
      setCuiIsDemo(false);
      setCuiRevealed(true);
      setSubmittedCui(cleaned);
    }
  }, [cuiValue]);

  const handleResetToDemo = useCallback(() => {
    setCuiIsDemo(true);
    setCuiRevealed(true);
    setCuiValue("");
    setSubmittedCui(undefined);
  }, []);

  const handleVerifyAnother = useCallback(() => {
    setCuiRevealed(false);
    setCuiValue("");
    setSubmittedCui(undefined);
  }, []);

  return (
    <section className="v40-cui v40-section-reveal" ref={sectionRef}>
      <div className="v40-inner">
        <p className="v40-eyebrow text-xs tracking-[1.4px] text-ds-blue-600 font-medium font-mono v40-reveal">VERIFICARE APLICABILITATE NIS2</p>
        <h2 className="v40-h2 font-heading text-ds-h2 font-bold tracking-[-1.3px] leading-[1.08] text-navy-900 v40-reveal v40-d1" style={{ marginTop: 16 }}>
          Verifica daca NIS2 se aplica companiei tale — evaluare rapida dupa CUI
        </h2>
        <p
          className="v40-body text-sm leading-[1.45] text-slate-700 v40-reveal v40-d2"
          style={{ marginTop: 16, maxWidth: 700 }}
        >
          Introdu codul unic de inregistrare (CUI) si primesti o evaluare
          preliminara de aplicabilitate NIS2, bazata pe codurile CAEN,
          dimensiunea intreprinderii si listele autoritatilor sectoriale din OUG
          155/2024.
        </p>
        <div className="v40-cui-search v40-reveal v40-d3">
          <input
            className="v40-cui-input font-mono text-sm text-navy-900"
            name="cui"
            autoComplete="on"
            placeholder="Introdu CUI (ex: 12345678)"
            aria-label="Introdu CUI (ex: 12345678)"
            value={cuiValue}
            onChange={(e) => setCuiValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCuiVerify()}
          />
          <button
            type="button"
            className={`v40-btn v40-btn-primary v40-hero-cta${cuiIsDemo ? " v40-btn-pulse" : ""}`}
            style={{ padding: "10px 20px" }}
            onClick={handleCuiVerify}
          >
            Verifica
          </button>
        </div>
        <p className="v40-cui-note text-ds-xxs text-ds-text-500 v40-reveal v40-d4">
          Datele sunt preluate din surse publice (ONRC / ANAF). Nu stocam
          informatii fara cont creat. Verificarea NIS2 este gratuita.
          {!cuiIsDemo && (
            <button
              type="button"
              className="v40-cui-demo-link text-xs text-ds-blue-600"
              onClick={handleResetToDemo}
            >
              Vezi exemplu demonstrativ
            </button>
          )}
        </p>

        <div
          className={`v40-cui-result-wrap${cuiRevealed ? " revealed" : ""}`}
          aria-live="polite"
          aria-atomic="true"
        >
          <div
            className="v40-cui-result"
            role="region"
            aria-label="Rezultat verificare CUI"
          >
            {!cuiIsDemo && (cuiLoading || (!companyData && !cuiError)) ? (
              <div
                className="v40-cui-skeleton"
                aria-label="Se incarca rezultatele..."
              >
                <div className="v40-cui-skeleton-bar warn" />
                <div className="v40-cui-skeleton-company">
                  <div className="v40-cui-skeleton-bar company-name" />
                  <div className="v40-cui-skeleton-bar badge" />
                </div>
                <div className="v40-cui-skeleton-bar info" />
                <div className="v40-cui-skeleton-grid">
                  <div className="v40-cui-skeleton-col">
                    <div className="v40-cui-skeleton-card tall" />
                    <div className="v40-cui-skeleton-card short" />
                  </div>
                  <div className="v40-cui-skeleton-col">
                    <div className="v40-cui-skeleton-bar label" />
                    <div
                      className="v40-cui-skeleton-service"
                      style={{ width: "90%" }}
                    />
                    <div
                      className="v40-cui-skeleton-service"
                      style={{ width: "75%" }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                {cuiIsDemo && (
                  <>
                    <span className="v40-cui-demo-label v40-cui-demo-label-shimmer text-ds-xxxs tracking-[1px] text-ds-text-400">
                      EXEMPLU DEMONSTRATIV
                    </span>
                    <span className="v40-cui-warn text-ds-xxs text-ds-amber-500">
                      ⚠ Evaluare preliminara — nu constituie decizie de
                      clasificare
                    </span>
                    <div className="v40-cui-company">
                      <span className="v40-cui-company-name font-heading text-ds-xxl font-bold text-white">
                        S.C. Companie Servicii S.R.L.
                      </span>
                      <span className="v40-cui-badge font-heading text-sm font-bold text-white bg-ds-blue-700">
                        ENTITATE IMPORTANTA — Certitudine: 78%
                      </span>
                    </div>
                    <p className="v40-cui-info text-xs text-ds-text-400">
                      CUI: 44012389 | CAEN: 6201 — Activitati de realizare a
                      soft-ului | Jud. Bucuresti
                    </p>
                    <div className="v40-cui-grid">
                      <div className="v40-cui-col">
                        <div className="v40-cui-card">
                          <span className="v40-cui-card-label text-ds-xxs font-medium text-ds-blue-300 tracking-[0.9px]">
                            Date provenite din surse publice
                          </span>
                          <span className="v40-cui-card-body text-ds-xxs text-ds-text-300">
                            Informatiile afisate sunt agregate din registre
                            publice (ONRC, ANAF si surse sectoriale) si au
                            caracter orientativ.
                          </span>
                        </div>
                        <div className="v40-cui-card">
                          <span className="v40-cui-card-label text-ds-xxs font-medium text-ds-blue-300 tracking-[0.9px]">
                            Profil Companie
                          </span>
                          <span
                            className="v40-cui-card-body text-ds-xxs text-ds-text-300"
                            style={{ color: "#E2E8F0", fontSize: 13 }}
                          >
                            Intreprindere Mare — Angajati: 320 · Cifra afaceri:
                            48M EUR
                          </span>
                        </div>
                      </div>
                      <div className="v40-cui-col">
                        <span
                          className="v40-cui-card-label text-ds-xxs font-medium text-ds-blue-300 tracking-[0.9px]"
                          style={{ color: "#2563EB" }}
                        >
                          SERVICII IDENTIFICATE — ANEXA 1
                        </span>
                        <div className="v40-cui-service">
                          <span className="v40-cui-service-name text-xs text-ds-text-300">
                            Furnizare servicii TIC gestionate
                          </span>
                          <span className="v40-cui-service-meta text-ds-xxs text-ds-text-400">
                            108 · Infra digitala · 88%
                          </span>
                        </div>
                        <div className="v40-cui-service">
                          <span className="v40-cui-service-name text-xs text-ds-text-300">
                            Furnizare servicii de securitate
                          </span>
                          <span className="v40-cui-service-meta text-ds-xxs text-ds-text-400">
                            108 · Infra digitala · 76%
                          </span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {!cuiIsDemo && cuiError && (
                  <div className="v40-cui-error text-sm text-[#FCA5A5]">
                    Nu am putut obtine date pentru CUI-ul introdus. Verifica
                    numarul si incearca din nou.
                  </div>
                )}

                {!cuiIsDemo && !cuiError && companyData && (
                  <>
                    <span className="v40-cui-warn text-ds-xxs text-ds-amber-500">
                      ⚠ Evaluare preliminara — nu constituie decizie de
                      clasificare
                    </span>
                    <div className="v40-cui-company">
                      <span className="v40-cui-company-name font-heading text-ds-xxl font-bold text-white">
                        {companyData.company.name ?? submittedCui}
                      </span>
                      <div className="v40-cui-labels">
                        <div className="v40-cui-label-group">
                          <span className="v40-cui-label-title text-ds-xxs text-ds-text-400 tracking-[0.05em]">
                            Încadrarea:
                          </span>
                          <span className="v40-cui-badge font-heading text-sm font-bold text-white bg-ds-blue-700">
                            {companyData.nis2Classification
                              ? NIS2_BADGE_LABELS[
                                  companyData.nis2Classification.classification
                                ]
                              : "CLASIFICARE INDISPONIBILĂ"}
                          </span>
                        </div>
                        <div className="v40-cui-label-group">
                          <span className="v40-cui-label-title text-ds-xxs text-ds-text-400 tracking-[0.05em]">
                            Nivel risc:
                          </span>
                          <span className="v40-cui-badge font-heading text-sm font-bold text-white bg-ds-blue-700">
                            {companyData.riskAnalysis
                              ? companyData.riskAnalysis.globalLevel
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="v40-cui-info text-xs text-ds-text-400">
                      CUI: {companyData.company.cui}
                      {companyData.company.caenPrincipal &&
                        ` | CAEN: ${companyData.company.caenPrincipal.code} — ${companyData.company.caenPrincipal.description}`}
                    </p>
                    <div className="v40-cui-grid">
                      <div className="v40-cui-col">
                        <div className="v40-cui-card">
                          <span className="v40-cui-card-label text-ds-xxs font-medium text-ds-blue-300 tracking-[0.9px]">
                            Date provenite din surse publice
                          </span>
                          <span className="v40-cui-card-body text-ds-xxs text-ds-text-300">
                            Informatiile afisate sunt agregate din registre
                            publice (ONRC, ANAF si surse sectoriale) si au
                            caracter orientativ.
                          </span>
                        </div>
                        {companyData.size.value && (
                          <div className="v40-cui-card">
                            <span className="v40-cui-card-label text-ds-xxs font-medium text-ds-blue-300 tracking-[0.9px]">
                              Profil Companie
                            </span>
                            <span
                              className="v40-cui-card-body text-ds-xxs text-ds-text-300"
                              style={{ color: "#E2E8F0", fontSize: 13 }}
                            >
                              Intreprindere {companyData.size.value}
                              {companyData.size.explanation &&
                                ` — ${companyData.size.explanation}`}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="v40-cui-col">
                        {companyData.matchedSectors.length > 0 ? (
                          (() => {
                            const allServices =
                              companyData.matchedSectors.flatMap((sector) =>
                                sector.services.map((svc) => ({
                                  ...svc,
                                  sectorName: sector.sectorName,
                                }))
                              );
                            const sorted = [...allServices].sort(
                              (a, b) => b.scorIncredere - a.scorIncredere
                            );
                            const top5 = sorted.slice(0, 5);
                            const remaining = sorted.length - top5.length;

                            return (
                              <>
                                <span
                                  className="v40-cui-card-label text-ds-xxs font-medium text-ds-blue-300 tracking-[0.9px]"
                                  style={{ color: "#2563EB" }}
                                >
                                  SERVICII IDENTIFICATE
                                </span>
                                {top5.map((svc) => (
                                  <div
                                    key={svc.entityId}
                                    className="v40-cui-service"
                                  >
                                    <span className="v40-cui-service-name text-xs text-ds-text-300">
                                      {svc.name}
                                    </span>
                                    <span className="v40-cui-service-meta text-ds-xxs text-ds-text-400">
                                      {svc.entityId} · {svc.sectorName} ·{" "}
                                      {svc.scorIncredere}%
                                    </span>
                                  </div>
                                ))}
                                {remaining > 0 && (
                                  <span
                                    className="v40-cui-service-meta text-ds-xxs text-ds-text-400"
                                    style={{ marginTop: 4 }}
                                  >
                                    + {remaining} alte servicii
                                  </span>
                                )}
                              </>
                            );
                          })()
                        ) : (
                          <span className="v40-cui-card-label text-ds-xxs font-medium text-ds-blue-300 tracking-[0.9px]">
                            Niciun serviciu NIS2 identificat pentru acest CUI.
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {!cuiIsDemo && (companyData || cuiError) && (
          <div className={`v40-cui-ctas-wrap${cuiRevealed ? " revealed" : ""}`}>
            {cuiArchivedMatch && (
              <div className="flex items-center gap-2 rounded-lg border border-ds-amber-500/30 bg-ds-amber-100 p-3 mb-3">
                <span className="text-sm text-navy-800">
                  Această companie este arhivată. Dezarhiveaz-o din Companiile
                  mele pentru a continua.
                </span>
              </div>
            )}
            <div className="v40-cui-ctas">
              <button
                type="button"
                className="v40-btn v40-btn-primary v40-hero-cta"
                disabled={isCreatingSession || cuiArchivedMatch}
                onClick={() =>
                  submittedCui && onCreateAccountWithCui(submittedCui)
                }
              >
                {isCreatingSession
                  ? "Se creeaza..."
                  : cuiArchivedMatch
                    ? "Companie arhivată"
                    : cuiAlreadyExists
                      ? "Continua evaluarea"
                      : isLoggedIn
                        ? "Adauga companie — evaluare completa"
                        : "Creeaza cont — evaluare completa gratuita"}
              </button>
              <button
                type="button"
                className="v40-btn v40-btn-secondary font-medium text-sm font-mono bg-[#F2F3F0] text-[#111]"
                onClick={handleVerifyAnother}
              >
                Verifica alt CUI
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
